/**
 * Templates page — detected page templates with quality bands and evidence.
 */

import { useParams, Link } from "react-router-dom";
import { useLatestTemplates } from "../hooks/use-templates.js";
import { WarningPanel } from "../components/warning-panel.js";
import { PageSkeleton } from "../components/skeleton.js";

const QUALITY_BAND_DESCRIPTIONS: Record<string, string> = {
  high: "Strong clustering signal — templates are structurally distinct and well-defined.",
  medium:
    "Moderate clustering signal — templates may overlap; results are usable but imprecise.",
  low: "Weak clustering signal — interpret template groupings with caution.",
};

export function TemplatesPage() {
  const { websiteId } = useParams<{ websiteId: string }>();

  const { data: run, isLoading, isError: runError } = useLatestTemplates(websiteId);

  if (isLoading) return <PageSkeleton />;

  if (!run) {
    return (
      <div className="product-page">
        <h1 className="page-title">Templates</h1>
        {runError ? (
          <div className="alert alert--error" role="alert">
            Failed to load template data. Try refreshing the page.
          </div>
        ) : (
        <>
        <p className="text-muted">
          No templates detected yet. Templates are page types that share a
          common layout (e.g. product pages, blog posts). They are detected
          automatically when you run a scan.
        </p>
        <Link
          to={`/websites/${websiteId}/scan`}
          className="btn btn--primary mt-2"
        >
          Run a scan
        </Link>
        </>
        )}
      </div>
    );
  }

  return (
    <div className="product-page">
      <h1 className="page-title">Templates</h1>
      <p className="page-subtitle">
        Templates are page types that share a common layout. The scan groups
        your pages by structure so that accessibility issues can be reported
        per template rather than per individual page.
      </p>
      <p className="text-muted text-sm">
        Detected from {run.fetchedCount} fetched {run.fetchedCount === 1 ? "URL" : "URLs"}.
      </p>

      {/* ── Run stats ──────────────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="tpl-stats-h">
        <h2 id="tpl-stats-h" className="section-title">
          Detection summary
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">{run.candidateCount}</span>
            <span className="stat-item__label">Candidates</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.fetchedCount}</span>
            <span className="stat-item__label">Fetched</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.templateCount}</span>
            <span className="stat-item__label">Templates detected</span>
          </div>
        </div>
      </section>

      {/* ── Warnings ───────────────────────────────────────────────────────── */}
      {run.warnings.length > 0 && (
        <section className="product-section">
          <WarningPanel warnings={run.warnings} label="Detection notices" />
        </section>
      )}

      {/* ── Template list ──────────────────────────────────────────────────── */}
      {run.templates.length > 0 ? (
        <section className="product-section" aria-labelledby="tpl-list-h">
          <h2 id="tpl-list-h" className="section-title">
            Detected templates
          </h2>
          <ul className="template-list">
            {run.templates.map((t) => (
              <li key={t.id} className="template-row">
                <div className="template-row__head">
                  <span className="template-row__label">{t.label}</span>
                  <span
                    className={`quality-band quality-band--${t.qualityBand}`}
                    title={QUALITY_BAND_DESCRIPTIONS[t.qualityBand]}
                  >
                    {t.qualityBand}
                  </span>
                  <span className="text-muted text-sm">
                    {t.memberCount} {t.memberCount === 1 ? "page" : "pages"}
                  </span>
                </div>
                {t.urlPattern && (
                  <div className="template-row__pattern text-sm text-muted">
                    Pattern: <code>{t.urlPattern}</code>
                  </div>
                )}
                {t.representatives.length > 0 && (
                  <div className="template-row__reps mt-2">
                    <span className="text-sm text-muted">Representatives:</span>
                    <ul className="rep-list">
                      {t.representatives.map((r) => (
                        <li key={r.canonicalUrl} className="rep-list__item">
                          <a
                            href={r.canonicalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm"
                          >
                            {r.canonicalUrl}
                          </a>
                          {r.reason && (
                            <span className="text-muted text-sm">
                              {" "}
                              — {r.reason}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="text-muted">No templates detected in this run.</p>
      )}

      {/* ── Related pages ────────────────────────────────────────────────── */}
      <section className="product-section">
        <div className="action-links">
          <Link
            to={`/websites/${websiteId}/report`}
            className="btn btn--secondary"
          >
            Full report
          </Link>
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            Dashboard
          </Link>
        </div>
      </section>
    </div>
  );
}
